import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useI18n } from "@/context/I18nContext";
import { maskProtectedTerms, restoreProtectedTerms } from "@/i18n/protectedTerms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_PREFIX = "e65_tx_v3_"; // bumped — flushes old caches that may contain pre-protection translations

// In-memory dedupe of in-flight requests so we never request the same string twice
const inflight = new Map();

/**
 * Run a translation with built-in protection for official titles & names.
 * Returns a Promise<string> of the translated text where every official term
 * (e.g. "Blue (Da Ba Dee)", "Eiffel 65", "Zorotl") is preserved verbatim.
 */
function translateProtected(text, lang, source) {
  const { masked, terms } = maskProtectedTerms(text);
  // Short-circuit: if the entire text is a single protected term (or only
  // protected tokens), nothing to translate.
  const stripped = masked.replace(/\u00abE65T\d+\u00bb/g, "").trim();
  if (!stripped) return Promise.resolve(text);

  return axios
    .post(`${API}/translate`, { text: masked, target_lang: lang, source_lang: source })
    .then((r) => {
      const translated = r.data?.translated || masked;
      return restoreProtectedTerms(translated, terms);
    })
    .catch(() => text);
}

/**
 * Tx — silent auto-translation for STATIC UI labels.
 *
 * Renders `children` (string) immediately. If the current language differs
 * from `source`, asks the backend `/api/translate` (Emergent LLM, cached in
 * MongoDB AND in localStorage). Re-renders once the translation arrives.
 *
 * Protected terms (official song/album/band/character names) are preserved
 * verbatim across all languages.
 *
 * Usage:
 *   <Tx>Tour History</Tx>
 *   <Tx source="de">Diskografie</Tx>
 */
export default function Tx({ children, source = "en", className, as: Tag = "span", ...rest }) {
  const { lang } = useI18n();
  const text = typeof children === "string" ? children : String(children ?? "");
  const [out, setOut] = useState(text);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!text) { setOut(text); return; }
    if (!lang || lang === source) { setOut(text); return; }

    const key = LS_PREFIX + lang + "_" + (typeof btoa === "function" ? btoa(unescape(encodeURIComponent(text))).slice(0, 220) : text.slice(0, 220));
    try {
      const cached = localStorage.getItem(key);
      if (cached) { setOut(cached); return; }
    } catch { /* ignore */ }

    setOut(text);

    const cacheKey = `${lang}::${text}`;
    let promise = inflight.get(cacheKey);
    if (!promise) {
      promise = translateProtected(text, lang, source).finally(() => inflight.delete(cacheKey));
      inflight.set(cacheKey, promise);
    }
    promise.then((tr) => {
      if (!mounted.current) return;
      if (tr && tr !== text) {
        setOut(tr);
        try { localStorage.setItem(key, tr); } catch { /* ignore */ }
      }
    });
  }, [text, lang, source]);

  return <Tag className={className} {...rest}>{out}</Tag>;
}

/** Imperative form: returns translated text via a hook. */
export function useTx(text, source = "en") {
  const { lang } = useI18n();
  const [out, setOut] = useState(text || "");

  useEffect(() => {
    if (!text) { setOut(""); return; }
    if (!lang || lang === source) { setOut(text); return; }

    const key = LS_PREFIX + lang + "_" + (typeof btoa === "function" ? btoa(unescape(encodeURIComponent(text))).slice(0, 220) : text.slice(0, 220));
    try {
      const cached = localStorage.getItem(key);
      if (cached) { setOut(cached); return; }
    } catch { /* ignore */ }

    setOut(text);
    const cacheKey = `${lang}::${text}`;
    let promise = inflight.get(cacheKey);
    if (!promise) {
      promise = translateProtected(text, lang, source).finally(() => inflight.delete(cacheKey));
      inflight.set(cacheKey, promise);
    }
    promise.then((tr) => {
      if (tr && tr !== text) {
        setOut(tr);
        try { localStorage.setItem(key, tr); } catch { /* ignore */ }
      }
    });
  }, [text, lang, source]);

  return out;
}
